import axios from 'axios';

const API_URL = 'https://apibooking.gondola-ancol.com/api';

// Create axios instance with default headers
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login page
      window.location.href = '/auth-login';
    }
    return Promise.reject(error);
  }
);

// Auth service functions
const authService = {
  // Register a new user
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'An error occurred during registration' };
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'An error occurred during login' };
    }
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Request password reset
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to process password reset request' };
    }
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Check if user is logged in
  isLoggedIn: () => {
    return !!localStorage.getItem('token');
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      // Use /auth/me/update endpoint instead of /auth/profile
      const response = await api.post('/auth/me/update', userData);
      // Update stored user data if successful
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update profile' };
    }
  },

  // Generate referral code
  generateReferral: async () => {
    try {
      const response = await api.post('/referrals/generate');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to generate referral code' };
    }
  },

  // Get user's referral code
  getUserReferral: async () => {
    try {
      const response = await api.get('/referrals/user');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get referral code' };
    }
  },

  // Get user's commission stats
  getCommissionStats: async () => {
    try {
      const response = await api.get('/commissions/user-stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get commission stats' };
    }
  },
  
  // Get user's commissions
  getCommissions: async (params = {}) => {
    try {
      const response = await api.get('/commissions', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get commissions' };
    }
  },
  
  // Get detailed commission statistics
  getDetailedCommissionStats: async () => {
    try {
      const response = await api.get('/commissions/user-stats');
      
      // Log the raw response for debugging
      console.log('Raw commission stats:', response.data);
      
      // Process the data appropriately
      const stats = {
        availableBalance: parseFloat(response.data.available_balance) || 0,
        totalEarned: parseFloat(response.data.total_earned) || 0,
        pendingAmount: parseFloat(response.data.pending_amount) || 0,
        pendingTransactions: parseInt(response.data.pending_transactions) || 0,
        totalTransactions: parseInt(response.data.total_transactions) || 0,
        
        // Add computed fields
        paidAmount: parseFloat(response.data.total_earned - response.data.pending_amount) || 0,
        paidTransactions: parseInt(response.data.total_transactions - response.data.pending_transactions) || 0,
        
        // For display purposes
        formattedBalance: new Intl.NumberFormat('id-ID', { 
          style: 'currency', 
          currency: 'IDR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(response.data.available_balance || 0),
        
        commissionPerTicket: 5000 // Fixed commission of Rp 5,000 per ticket
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching detailed commission stats:', error);
      throw error.response?.data || { message: 'Failed to get detailed commission stats' };
    }
  },
  
  // Get user's withdrawals
  getWithdrawals: async (params = {}) => {
    try {
      const response = await api.get('/withdrawals', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get withdrawals' };
    }
  },
  
  // Request a withdrawal
  requestWithdrawal: async (withdrawalData) => {
    try {
      const response = await api.post('/withdrawals', withdrawalData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to request withdrawal' };
    }
  },
  
  // Cancel a withdrawal request
  cancelWithdrawal: async (id) => {
    try {
      const response = await api.post(`/withdrawals/${id}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to cancel withdrawal' };
    }
  },
  
  // Get a specific withdrawal by ID
  getWithdrawalDetails: async (id) => {
    try {
      const response = await api.get(`/withdrawals/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get withdrawal details' };
    }
  },

  // Get commission details by ID
  getCommissionDetails: async (id) => {
    try {
      const response = await api.get(`/commissions/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get commission details' };
    }
  },

  // Get booking details by ID
  getBookingDetails: async (id) => {
    try {
      const response = await api.get(`/bookings/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get booking details' };
    }
  },

  // Verify if a user has completed their profile
  verifyProfileCompletion: async () => {
    try {
      // Use /auth/me endpoint to get current user profile data
      const response = await api.get('/auth/me');
      
      // Get user from response - API now consistently returns { user: {...} }
      const user = response.data.user;
      
      // Check if required fields are present to determine if profile is complete
      const requiredFields = ['name', 'email', 'phone', 'bank_name', 'bank_account_number', 
                             'bank_account_name', 'birth_date', 'address'];
      
      const isComplete = requiredFields.every(field => 
        user[field] && String(user[field]).trim() !== ''
      );
      
      return { isComplete, user };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to verify profile completion' };
    }
  }
};

export default authService; 